import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useT } from "../lib/i18n";
import { Search, Smartphone, BadgePercent } from "lucide-react";

const DEVICE_FALLBACK = "https://images.pexels.com/photos/3945672/pexels-photo-3945672.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Devices() {
  const { t } = useT();
  const [devices, setDevices] = useState([]);
  const [promos, setPromos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [brand, setBrand] = useState("all");
  const [categoryId, setCategoryId] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [d, p, c] = await Promise.all([
          api.get("/devices", { params: { active_only: true } }),
          api.get("/promos", { params: { active_only: true } }),
          api.get("/categories", { params: { active_only: true } }),
        ]);
        setDevices(d.data || []);
        setPromos(p.data || []);
        setCategories(c.data || []);
      } finally { setLoading(false); }
    })();
  }, []);

  const promosByDevice = useMemo(() => {
    const m = {};
    promos.forEach((p) => {
      if (!p.device_id) return;
      m[p.device_id] = m[p.device_id] || [];
      m[p.device_id].push(p);
    });
    return m;
  }, [promos]);

  // "BYO ___" and "Existing ___" devices are placeholder rows used by the Bundle wizard
  // for add-a-line / bring-your-own pricing flows. They have no MSRP, no promos, no images and
  // make no sense in the public catalog, so hide them here.
  const isPlaceholderDevice = (d) => {
    const m = (d.model || "").toLowerCase();
    const b = (d.brand || "").toLowerCase();
    return m.includes("byo") || b.includes("byo") || m.includes("existing") || b.includes("existing");
  };
  const brands = useMemo(
    () => Array.from(new Set(devices.filter((d) => !isPlaceholderDevice(d)).map((d) => d.brand))).sort(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [devices]
  );
  const filtered = useMemo(() => {
    return devices.filter((d) => {
      if (isPlaceholderDevice(d)) return false;
      if (brand !== "all" && d.brand !== brand) return false;
      if (categoryId !== "all" && (d.category_id || "") !== categoryId) return false;
      if (q && !`${d.brand} ${d.model}`.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [devices, brand, categoryId, q]);

  return (
    <main className="min-h-[80vh]" data-testid="page-devices">
      {/* header */}
      <section className="bg-[#0B132B] text-white border-b border-slate-200">
        <div className="tw-container py-16">
          <div className="tw-overline !text-[#00E5FF]">{t("devices_overline")}</div>
          <h1 className="mt-2 text-5xl sm:text-6xl font-extrabold tracking-tighter">
            {t("devices_h1_a")} <span className="text-[#00E5FF]">{t("devices_h1_b")}</span>
          </h1>
          <p className="mt-4 text-slate-300 max-w-xl">
            {t("devices_subtitle")}
          </p>
        </div>
      </section>

      <section className="tw-container py-10">
        {categories.length > 0 && (
          <div className="flex gap-2 flex-wrap mb-6" data-testid="category-filter">
            <button
              onClick={() => setCategoryId("all")}
              className={`text-sm font-semibold px-4 py-2 border-2 transition-all ${categoryId === "all" ? "border-[#0B132B] bg-[#0B132B] text-white" : "border-slate-200 text-slate-600 hover:border-[#0B132B] hover:text-[#0B132B]"}`}
              data-testid="category-filter-all"
            >
              {t("devices_filter_all")}
            </button>
            {categories.map((c) => (
              <button
                key={c.id}
                onClick={() => setCategoryId(c.id)}
                className={`text-sm font-semibold px-4 py-2 border-2 transition-all ${categoryId === c.id ? "border-[#0B132B] bg-[#0B132B] text-white" : "border-slate-200 text-slate-600 hover:border-[#0B132B] hover:text-[#0B132B]"}`}
                data-testid={`category-filter-${c.id}`}
              >
                {c.name}
              </button>
            ))}
          </div>
        )}
        <div className="flex flex-col md:flex-row gap-4 md:items-center justify-between mb-8" data-testid="devices-filters">
          <div className="relative md:w-96">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="input-sharp !pl-10"
              placeholder={t("devices_search_ph")}
              data-testid="devices-search-input"
            />
          </div>
          <div className="flex gap-2 flex-wrap" data-testid="brand-filter">
            <button
              onClick={() => setBrand("all")}
              className={`badge-pill ${brand === "all" ? "badge-navy" : "badge-outline"} cursor-pointer`}
              data-testid="brand-filter-all"
            >
              {t("devices_filter_all")}
            </button>
            {brands.map((b) => (
              <button
                key={b}
                onClick={() => setBrand(b)}
                className={`badge-pill ${brand === b ? "badge-navy" : "badge-outline"} cursor-pointer`}
                data-testid={`brand-filter-${b.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {b}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="py-20 text-center text-slate-500" data-testid="devices-loading">Loading devices…</div>
        ) : filtered.length === 0 ? (
          <div className="surface-card text-center py-20" data-testid="devices-empty">
            <Smartphone className="h-8 w-8 mx-auto text-slate-400" />
            <p className="mt-4 text-slate-600">{t("devices_empty")}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((d) => {
              const ps = promosByDevice[d.id] || [];
              const pp = d.phone_pricing;
              const scenarioKeys = pp ? ["finance", "switch", "join", "no_hassle", "full_retail"].filter((k) => pp[k]?.enabled) : [];
              const fromPrice = scenarioKeys.length > 0 ? Math.min(...scenarioKeys.map((k) => {
                const s = pp[k];
                if (k === "finance") {
                  const m = Math.max(1, parseInt(s.months || 24, 10));
                  return Math.max(0, ((s.device_price || 0) - (s.fcot_credit || 0)) / m);
                }
                return s.price || 0;
              })) : null;
              const fromIsMonthly = scenarioKeys.includes("finance") && fromPrice !== null && (() => {
                const f = pp.finance;
                const m = Math.max(1, parseInt(f.months || 24, 10));
                const mo = Math.max(0, ((f.device_price || 0) - (f.fcot_credit || 0)) / m);
                return mo <= fromPrice + 0.0001;
              })();
              return (
                <Link
                  to={`/devices/${d.id}`}
                  key={d.id}
                  className="surface-card !p-0 group"
                  data-testid={`device-card-${d.id}`}
                >
                  <div className="aspect-square bg-slate-50 overflow-hidden relative">
                    <img
                      src={d.image_url || DEVICE_FALLBACK}
                      alt={`${d.brand} ${d.model}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    {ps.length > 0 && (
                      <span className="absolute top-3 left-3 badge-pill badge-teal inline-flex items-center gap-1">
                        <BadgePercent className="h-3 w-3" /> {ps.length} promo{ps.length > 1 ? "s" : ""}
                      </span>
                    )}
                    {scenarioKeys.length > 0 && (
                      <span className="absolute top-3 right-3 badge-pill badge-navy inline-flex items-center gap-1" data-testid={`device-card-paths-${d.id}`}>
                        {scenarioKeys.length} path{scenarioKeys.length > 1 ? "s" : ""}
                      </span>
                    )}
                  </div>
                  <div className="p-5">
                    <div className="tw-overline">{d.brand}</div>
                    <div className="mt-1 text-lg font-bold text-[#0B132B]">{d.model}</div>
                    {fromPrice !== null ? (
                      <div className="mt-3 flex items-baseline justify-between" data-testid={`device-card-from-${d.id}`}>
                        <div>
                          <div className="text-[10px] mono uppercase tracking-widest text-slate-500">from</div>
                          <div className="mono text-2xl font-bold text-[#0B132B] ticker-num">
                            ${fromPrice.toFixed(2)}{fromIsMonthly && <span className="text-xs text-slate-500 font-normal">/mo</span>}
                          </div>
                        </div>
                        <div className="text-xs text-slate-500 mono text-right">
                          <div>MSRP ${d.msrp.toFixed(0)}</div>
                          {d.storage && <div>{d.storage}</div>}
                        </div>
                      </div>
                    ) : (
                      <div className="mt-3 flex items-baseline justify-between">
                        <div className="mono text-2xl font-bold text-[#0B132B] ticker-num">${d.msrp.toFixed(0)}</div>
                        <div className="text-xs text-slate-500 mono">{d.storage}</div>
                      </div>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </section>
    </main>
  );
}
